// react
import { useRef, useState } from 'react';
// react-hook-form
import { Controller, useWatch } from 'react-hook-form';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';

// material ui
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// api
import { useLazySensorsGetAsyncByidQuery } from '@/api/generated/recalc/endpoints/v1/sensors';

// shared components
import {
  RHFFieldLabel,
  RHFNumberField,
  RHFSelect,
  RHFTextField,
} from '@/components/ui/form';
import InfoTooltipIcon from '@/components/ui/icon/InfoTooltipIcon';

// feature-components
import DataConversionDialog from '@/features/sensors/components/DataConversionDialog';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import type {
  RecordedDataType,
  SelectedUnitType,
  UnitInputMode,
  UnitTypeInputFormValue,
} from './unitTypeInput.schema';

const ANALOG_DATA_TYPES = ['analog-current', 'analog-voltage'];

// This component is shared by both CreateUnitForm and EditUnitForm, whose
// form-value shapes differ (`CreateUnitFormValues` vs `EditUnitFormValues`)
// but both always include a `unitTypeInputs: Record<string,
// UnitTypeInputFormValue>` field - hence the `TFieldValues` generic
// constrained to that shape below.
export type UnitTypeInputsFieldValues = FieldValues & {
  unitTypeInputs: Record<string, UnitTypeInputFormValue>;
};

// react-hook-form's `FieldPath<T>` is derived from `T`'s literal key shape,
// which doesn't statically enumerate the dynamic numeric keys of a
// `Record<string, UnitTypeInputFormValue>` (z.record) field. Every dot-path
// name below (e.g. `unitTypeInputs.42.sensorId`) is a perfectly valid runtime
// path for RHF, so this narrow cast is a deliberate, contained escape hatch
// rather than a widespread `any`.
function fieldPath<TFieldValues extends UnitTypeInputsFieldValues>(
  path: string,
): FieldPath<TFieldValues> {
  return path as unknown as FieldPath<TFieldValues>;
}

// Loosely-typed imperative read/write helpers, bound to `unitTypeInputs.*`
// dot-paths only. Kept separate from RHF's own strictly-typed
// `UseFormGetValues`/`UseFormSetValue` (which suffer the same Record-path
// gap as `FieldPath` above) - `CreateUnitForm` passes its real `getValues`/
// `setValue` in, cast once at that single boundary.
export type UnitTypeInputsGetValues = (path: string) => unknown;
export type UnitTypeInputsSetValue = (
  path: string,
  value: unknown,
  options?: { shouldValidate?: boolean; shouldDirty?: boolean },
) => void;

type SensorDto = {
  ChannelCount?: number;
  UnitSensors?: { ChannelNumber?: number }[];
};

function normalizeChannelNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

// Pure - takes the sibling snapshot explicitly so it can be called both
// reactively (with a `useWatch`-ed snapshot, for render) and imperatively
// (with a fresh `getValues('unitTypeInputs')` read, from the async sensor
// fetch's completion handler).
function calculateAvailableChannels(
  inputId: number,
  sensorId: string,
  sensor: SensorDto,
  currentChannelNumber: number | null | undefined,
  unitTypeInputsSnapshot: Record<string, UnitTypeInputFormValue>,
): number[] {
  const channelCount = Number(sensor.ChannelCount) || 0;
  if (channelCount === 0) return [];

  const usedInDatabase = (sensor.UnitSensors ?? [])
    .map((unitSensor) => normalizeChannelNumber(unitSensor.ChannelNumber))
    .filter((channel): channel is number => channel != null);

  const usedInForm = Object.entries(unitTypeInputsSnapshot)
    .filter(
      ([otherId, data]) =>
        parseInt(otherId, 10) !== inputId &&
        data.sensorId === sensorId &&
        Boolean(data.channelNumber) &&
        data.inputType === 'sensorId',
    )
    .map(([, data]) => normalizeChannelNumber(data.channelNumber))
    .filter((channel): channel is number => channel != null);

  const unavailable = [...usedInDatabase, ...usedInForm];
  const available: number[] = [];
  for (let channel = 1; channel <= channelCount; channel += 1) {
    if (!unavailable.includes(channel)) {
      available.push(channel);
    }
  }

  // Always keep the current channel selectable, even if it's "used" in the
  // database - it's used by THIS input, so it must remain in its own list.
  const normalizedCurrentChannel = normalizeChannelNumber(currentChannelNumber);
  if (
    normalizedCurrentChannel &&
    !available.includes(normalizedCurrentChannel)
  ) {
    available.push(normalizedCurrentChannel);
  }

  available.sort((a, b) => a - b);
  return available;
}

type UnitTypeInputsSectionProps<
  TFieldValues extends UnitTypeInputsFieldValues,
> = {
  control: Control<TFieldValues>;
  getValues: UnitTypeInputsGetValues;
  setValue: UnitTypeInputsSetValue;
  selectedUnitType: SelectedUnitType;
  recordedDataTypes: RecordedDataType[];
};

export default function UnitTypeInputsSection<
  TFieldValues extends UnitTypeInputsFieldValues,
>({
  control,
  getValues,
  setValue,
  selectedUnitType,
  recordedDataTypes,
}: UnitTypeInputsSectionProps<TFieldValues>) {
  const unitTypeInputsSnapshot = useWatch({
    control,
    name: fieldPath<TFieldValues>('unitTypeInputs'),
  }) as Record<string, UnitTypeInputFormValue>;

  const [dataConversionDialog, setDataConversionDialog] = useState<{
    open: boolean;
    sensorId: string | null;
    channelNumber: number | null;
    inputName: string | null;
  }>({ open: false, sensorId: null, channelNumber: null, inputName: null });

  const sortedInputs = [...(selectedUnitType.UnitTypeInputs ?? [])].sort(
    (a, b) => a.Name.localeCompare(b.Name),
  );

  return (
    <>
      <Stack spacing={1.5}>
        {sortedInputs.map((input) => {
          const inputRecordedDataTypes =
            recordedDataTypes.find(
              (recordedDataType) =>
                recordedDataType.UnitTypeInput?.UnitTypeInputId ===
                input.UnitTypeInputId,
            )?.DataTypes ?? [];
          const hasAnalogDataType = inputRecordedDataTypes.some((dataType) =>
            ANALOG_DATA_TYPES.includes(dataType),
          );

          return (
            <UnitTypeInputRow<TFieldValues>
              key={input.UnitTypeInputId}
              control={control}
              getValues={getValues}
              setValue={setValue}
              inputId={input.UnitTypeInputId}
              inputName={input.Name}
              unitTypeInputsSnapshot={unitTypeInputsSnapshot ?? {}}
              hasAnalogDataType={hasAnalogDataType}
              onOpenDataConversion={(sensorId, channelNumber) =>
                setDataConversionDialog({
                  open: true,
                  sensorId,
                  channelNumber,
                  inputName: input.Name,
                })
              }
            />
          );
        })}
      </Stack>

      <DataConversionDialog
        open={dataConversionDialog.open}
        onClose={() =>
          setDataConversionDialog((prev) => ({ ...prev, open: false }))
        }
        sensorId={dataConversionDialog.sensorId}
        channelNumber={dataConversionDialog.channelNumber}
        inputName={dataConversionDialog.inputName}
      />
    </>
  );
}

type UnitTypeInputRowProps<TFieldValues extends UnitTypeInputsFieldValues> = {
  control: Control<TFieldValues>;
  getValues: UnitTypeInputsGetValues;
  setValue: UnitTypeInputsSetValue;
  inputId: number;
  inputName: string;
  unitTypeInputsSnapshot: Record<string, UnitTypeInputFormValue>;
  hasAnalogDataType: boolean;
  onOpenDataConversion: (
    sensorId: string,
    channelNumber: number | null,
  ) => void;
};

const EMPTY_INPUT_VALUE: UnitTypeInputFormValue = {
  inputType: 'sensorId',
  sensorId: '',
  externalSensorId: '',
  apiInput: false,
  customName: '',
  channelNumber: null,
};

function UnitTypeInputRow<TFieldValues extends UnitTypeInputsFieldValues>({
  control,
  getValues,
  setValue,
  inputId,
  inputName,
  unitTypeInputsSnapshot,
  hasAnalogDataType,
  onOpenDataConversion,
}: UnitTypeInputRowProps<TFieldValues>) {
  const { t } = useTranslation('units-gateways-sensors');
  const dispatch = useDispatch();
  const [getSensorById] = useLazySensorsGetAsyncByidQuery();
  const [sensor, setSensor] = useState<SensorDto | null>(null);
  const [isLoadingSensor, setIsLoadingSensor] = useState(false);
  const latestRequestRef = useRef(0);

  const inputData = unitTypeInputsSnapshot[inputId] ?? EMPTY_INPUT_VALUE;

  const sensorIdPath = `unitTypeInputs.${inputId}.sensorId`;
  const channelNumberPath = `unitTypeInputs.${inputId}.channelNumber`;
  const externalSensorIdPath = `unitTypeInputs.${inputId}.externalSensorId`;
  const customNamePath = `unitTypeInputs.${inputId}.customName`;
  const inputTypePath = `unitTypeInputs.${inputId}.inputType`;
  const apiInputPath = `unitTypeInputs.${inputId}.apiInput`;

  const availableChannels = inputData.sensorId
    ? sensor
      ? calculateAvailableChannels(
          inputId,
          inputData.sensorId,
          sensor,
          inputData.channelNumber,
          unitTypeInputsSnapshot,
        )
      : normalizeChannelNumber(inputData.channelNumber)
        ? [normalizeChannelNumber(inputData.channelNumber) as number]
        : []
    : normalizeChannelNumber(inputData.channelNumber)
      ? [normalizeChannelNumber(inputData.channelNumber) as number]
      : [];

  const canConfigureDataConversion =
    hasAnalogDataType &&
    inputData.inputType === 'sensorId' &&
    Boolean(inputData.sensorId) &&
    Boolean(inputData.channelNumber);

  const handleSensorIdChange = async (
    rawValue: string,
    updateSensorIdField: (value: string) => void,
  ) => {
    const nextSensorId = rawValue.trim();
    const previousSensorId = ((getValues(sensorIdPath) as string) ?? '').trim();

    updateSensorIdField(rawValue);

    if (previousSensorId && previousSensorId !== nextSensorId) {
      setValue(channelNumberPath, null, { shouldValidate: true });
    }

    if (!nextSensorId) {
      latestRequestRef.current += 1;
      setIsLoadingSensor(false);
      setSensor(null);
      return;
    }

    const requestId = ++latestRequestRef.current;
    setIsLoadingSensor(true);

    try {
      const fetchedSensor = (await getSensorById({
        id: Number(nextSensorId),
      }).unwrap()) as SensorDto | null;
      if (latestRequestRef.current !== requestId) return;

      if (fetchedSensor) {
        if (fetchedSensor.ChannelCount === fetchedSensor.UnitSensors?.length) {
          dispatch(
            showSnackbar({
              message: t('units.allChannelsUsed'),
              severity: 'warning',
            }),
          );
        }

        setSensor(fetchedSensor);

        const currentChannel = normalizeChannelNumber(
          getValues(channelNumberPath),
        );
        const latestUnitTypeInputs = getValues('unitTypeInputs') as Record<
          string,
          UnitTypeInputFormValue
        >;
        const nextAvailableChannels = calculateAvailableChannels(
          inputId,
          nextSensorId,
          fetchedSensor,
          currentChannel,
          latestUnitTypeInputs,
        );
        const channelToSet =
          currentChannel && nextAvailableChannels.includes(currentChannel)
            ? currentChannel
            : (nextAvailableChannels[0] ?? null);

        if (channelToSet !== currentChannel) {
          setValue(channelNumberPath, channelToSet, { shouldValidate: true });
        }
      } else {
        setSensor(null);
      }
    } catch {
      setSensor(null);
    } finally {
      if (latestRequestRef.current === requestId) {
        setIsLoadingSensor(false);
      }
    }
  };

  const handleInputTypeChange = (
    inputType: UnitInputMode,
    updateInputTypeField: (value: UnitInputMode) => void,
  ) => {
    updateInputTypeField(inputType);
    setValue(apiInputPath, inputType === 'apiInput', { shouldValidate: true });

    if (inputType !== 'sensorId') {
      setValue(sensorIdPath, '', { shouldValidate: true });
    }
    if (inputType !== 'externalSensorId') {
      setValue(externalSensorIdPath, '', { shouldValidate: true });
    }
    if (inputType === 'apiInput') {
      setValue(channelNumberPath, undefined, { shouldValidate: true });
    }
  };

  return (
    <Stack>
      <Box display="flex" alignItems="center">
        <Typography variant="overline" fontWeight={600}>
          {inputName}
        </Typography>
        <InfoTooltipIcon
          title={t('tooltips.unitTypeInputId', { id: inputId })}
        />
      </Box>

      <Box sx={{ mt: 1 }}>
        {inputData.inputType === 'sensorId' && (
          <Grid container spacing={2}>
            <Grid size={4}>
              {/* Bespoke Controller (not RHFTextField): typing a sensor ID
                  needs an async side effect - fetching the sensor by ID and
                  resetting/defaulting the sibling channelNumber field via
                  `handleSensorIdChange` - which none of the shared RHF field
                  wrappers has an escape hatch for (they hard-code
                  `onChange={field.onChange}`). */}
              <Controller
                name={fieldPath<TFieldValues>(sensorIdPath)}
                control={control}
                render={({ field, fieldState }) => (
                  <Box>
                    <RHFFieldLabel htmlFor={sensorIdPath}>
                      {t('sensors.sensorId')}
                    </RHFFieldLabel>
                    <TextField
                      id={sensorIdPath}
                      fullWidth
                      value={(field.value as string) ?? ''}
                      onChange={(event) =>
                        handleSensorIdChange(event.target.value, field.onChange)
                      }
                      onBlur={field.onBlur}
                      placeholder={t('units.enterSensorId')}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      data-testid="unit-type-input-sensor-id-textfield"
                    />
                  </Box>
                )}
              />
            </Grid>

            <Grid size={4}>
              <RHFSelect<TFieldValues, number>
                name={fieldPath<TFieldValues>(channelNumberPath)}
                control={control}
                fullWidth
                label={t('units.channelNumber')}
                data-testid="unit-type-input-channel-number-select"
                disabled={
                  !inputData.sensorId ||
                  availableChannels.length === 0 ||
                  isLoadingSensor
                }
                endAdornment={
                  isLoadingSensor ? (
                    <CircularProgress size={20} sx={{ mr: 3 }} />
                  ) : null
                }
                options={availableChannels.map((channelNum) => ({
                  value: channelNum,
                  label: `${t('units.channel')} ${channelNum}`,
                  testId: `unit-type-input-channel-option-${channelNum}`,
                }))}
              />
            </Grid>

            <Grid size={4}>
              <RHFTextField
                name={fieldPath<TFieldValues>(customNamePath)}
                control={control}
                fullWidth
                label={t('units.customNameOptional')}
                placeholder={t('units.enterCustomName')}
              />
            </Grid>

            {canConfigureDataConversion && (
              <Grid size={12}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SettingsRoundedIcon />}
                  onClick={() =>
                    onOpenDataConversion(
                      inputData.sensorId,
                      inputData.channelNumber ?? null,
                    )
                  }
                >
                  {t('sensors.configureDataConversion')}
                </Button>
              </Grid>
            )}
          </Grid>
        )}

        {inputData.inputType === 'externalSensorId' && (
          <Grid container spacing={2}>
            <Grid size={4}>
              <RHFTextField
                name={fieldPath<TFieldValues>(externalSensorIdPath)}
                control={control}
                fullWidth
                label={t('externalSensors.externalSensorId')}
                placeholder={t('units.enterExternalSensorId')}
              />
            </Grid>

            <Grid size={4}>
              <RHFNumberField
                name={fieldPath<TFieldValues>(channelNumberPath)}
                control={control}
                fullWidth
                label={t('units.channelNumber')}
              />
            </Grid>

            <Grid size={4}>
              <RHFTextField
                name={fieldPath<TFieldValues>(customNamePath)}
                control={control}
                fullWidth
                label={t('units.customNameOptional')}
                placeholder={t('units.enterCustomName')}
              />
            </Grid>
          </Grid>
        )}

        {inputData.inputType === 'apiInput' && (
          <Grid container spacing={2} alignItems="center">
            <Grid size={8}>
              <Box sx={{ py: 1.5 }}>
                <Typography variant="body2" color="primary">
                  {t('units.apiInputNoSensor')}
                </Typography>
              </Box>
            </Grid>

            <Grid size={4}>
              <RHFTextField
                name={fieldPath<TFieldValues>(customNamePath)}
                control={control}
                fullWidth
                label={t('units.customNameOptional')}
                placeholder={t('units.enterCustomName')}
              />
            </Grid>
          </Grid>
        )}

        {/* Bespoke Controller (no RHFRadioGroup wrapper exists yet): the
            radio value change needs to reset sibling sensorId/channelNumber
            state via `handleInputTypeChange`, which a plain RHF radio-group
            wrapper doesn't currently expose a hook for. */}
        <Controller
          name={fieldPath<TFieldValues>(inputTypePath)}
          control={control}
          render={({ field }) => (
            <FormControl component="fieldset" size="small" sx={{ mt: 2 }}>
              <FormLabel
                component="legend"
                sx={{ fontSize: '0.875rem', mb: 0.5 }}
              >
                {t('units.inputType')}
              </FormLabel>
              <RadioGroup
                row
                sx={{ gap: 2 }}
                value={(field.value as UnitInputMode) ?? 'sensorId'}
                onChange={(event) =>
                  handleInputTypeChange(
                    event.target.value as UnitInputMode,
                    field.onChange,
                  )
                }
              >
                <FormControlLabel
                  value="sensorId"
                  control={<Radio size="small" />}
                  label={t('sensors.sensorId')}
                  sx={{ mr: 0 }}
                />
                <FormControlLabel
                  value="externalSensorId"
                  control={<Radio size="small" />}
                  label={t('externalSensors.externalSensorId')}
                  sx={{ mr: 0 }}
                />
                <FormControlLabel
                  value="apiInput"
                  control={<Radio size="small" />}
                  label={t('units.apiInput')}
                  sx={{ mr: 0 }}
                />
              </RadioGroup>
            </FormControl>
          )}
        />
      </Box>
    </Stack>
  );
}
